/**
 * Centralized Animation Configuration
 *
 * All animation timing, easing, and class combinations are defined here
 * to ensure consistency across the application.
 *
 * Usage:
 * - Import animation classes into UI components
 * - Modify values here to tweak animations globally
 * - All durations respect prefers-reduced-motion via Tailwind
 */

// =============================================================================
// TIMING & EASING
// =============================================================================

/**
 * Animation durations in milliseconds
 * - fast: Micro-interactions, hover states
 * - normal: Standard transitions, dialogs
 * - slow: Page transitions, complex sequences
 */
export const DURATION = {
  fast: 150,
  normal: 200,
  slow: 300,
} as const;

/**
 * Tailwind duration classes
 */
export const DURATION_CLASS = {
  fast: 'duration-150',
  normal: 'duration-200',
  slow: 'duration-300',
} as const;

/**
 * CSS easing functions
 * - default: Standard ease-out for most animations
 * - spring: Slight overshoot for playful feel
 * - smooth: Ease-in-out for reversible animations
 */
export const EASING = {
  default: 'cubic-bezier(0.16, 1, 0.3, 1)', // ease-out with nice deceleration
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // slight overshoot
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-in-out
} as const;

// =============================================================================
// DIALOG ANIMATIONS
// =============================================================================

/**
 * Dialog/Modal overlay animation classes
 * Fades in/out the backdrop
 */
export const DIALOG_OVERLAY = [
  'data-[state=open]:animate-in',
  'data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0',
  'data-[state=closed]:fade-out-0',
  DURATION_CLASS.normal,
].join(' ');

/**
 * Dialog/Modal content animation classes
 * Clean fade + scale for centered modals
 */
export const DIALOG_CONTENT = [
  // Base transition
  DURATION_CLASS.slow,
  // Enter/exit states
  'data-[state=open]:animate-in',
  'data-[state=closed]:animate-out',
  // Fade
  'data-[state=open]:fade-in-0',
  'data-[state=closed]:fade-out-0',
  // Scale (zoom from 95% - subtle but visible)
  'data-[state=open]:zoom-in-95',
  'data-[state=closed]:zoom-out-95',
].join(' ');

// =============================================================================
// SHEET (SLIDE-OVER) ANIMATIONS
// =============================================================================

/**
 * Sheet overlay - same as dialog
 */
export const SHEET_OVERLAY = DIALOG_OVERLAY;

/**
 * Sheet content base classes (without directional slides)
 */
export const SHEET_CONTENT_BASE = [
  'transition',
  'ease-in-out',
  'data-[state=open]:animate-in',
  'data-[state=closed]:animate-out',
  `data-[state=closed]:${DURATION_CLASS.normal}`,
  `data-[state=open]:${DURATION_CLASS.slow}`,
].join(' ');

/**
 * Sheet directional slide classes by side
 */
export const SHEET_SLIDES = {
  top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
  bottom: 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
  left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  right: 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
} as const;

// =============================================================================
// DROPDOWN / POPOVER ANIMATIONS
// =============================================================================

/**
 * Dropdown menu content animation
 * Quick fade + scale with directional awareness
 */
export const DROPDOWN_CONTENT = [
  DURATION_CLASS.fast,
  'data-[state=open]:animate-in',
  'data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0',
  'data-[state=closed]:fade-out-0',
  'data-[state=open]:zoom-in-95',
  'data-[state=closed]:zoom-out-95',
  // Directional slides based on position
  'data-[side=top]:slide-in-from-bottom-2',
  'data-[side=bottom]:slide-in-from-top-2',
  'data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2',
].join(' ');

/**
 * Popover content animation (same as dropdown)
 */
export const POPOVER_CONTENT = DROPDOWN_CONTENT;

// =============================================================================
// SELECT ANIMATIONS (same as dropdown)
// =============================================================================

/**
 * Select content animation
 */
export const SELECT_CONTENT = DROPDOWN_CONTENT;

// =============================================================================
// TABLE ANIMATIONS
// =============================================================================

/**
 * Table row hover effect - subtle background transition
 */
export const TABLE_ROW_HOVER = 'transition-colors hover:bg-muted/50';

// =============================================================================
// SKELETON ANIMATIONS
// =============================================================================

/**
 * Skeleton loading shimmer effect
 * Requires the shimmer keyframe in tailwind.config.js
 */
export const SKELETON_SHIMMER = 'animate-shimmer bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%]';

/**
 * Simple pulse animation (default Tailwind)
 */
export const SKELETON_PULSE = 'animate-pulse';

/**
 * AI Processing Banner — use the <AIProcessingBanner> component instead of raw classes.
 * CSS animations are defined in globals.css: ai-shimmer-sweep, ai-sparkle.
 */
export const AI_PROCESSING = 'ai-processing-banner';

// =============================================================================
// COLLAPSIBLE ANIMATIONS
// =============================================================================

/**
 * Collapsible content animation (height transition)
 * Uses collapsible-specific keyframes that reference --radix-collapsible-content-height
 */
export const COLLAPSIBLE_CONTENT = [
  'overflow-hidden',
  'data-[state=open]:animate-collapsible-down',
  'data-[state=closed]:animate-collapsible-up',
].join(' ');

// =============================================================================
// UTILITY CLASSES
// =============================================================================

/**
 * Standard hover transition for interactive elements
 */
export const HOVER_TRANSITION = 'transition-colors duration-150';

/**
 * Focus ring transition
 */
export const FOCUS_TRANSITION = 'transition-shadow duration-150';

/**
 * Opacity transition for show/hide
 */
export const OPACITY_TRANSITION = `transition-opacity ${DURATION_CLASS.fast}`;

/**
 * Card hover effect - lift with shadow
 */
export const CARD_HOVER = 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md';

// =============================================================================
// MICRO-INTERACTIONS
// =============================================================================

/**
 * Button press effect - subtle scale down on active
 */
export const BUTTON_PRESS = 'active:scale-[0.98] transition-transform duration-75';

/**
 * Icon button hover - scale up slightly
 */
export const ICON_HOVER = 'transition-transform duration-150 hover:scale-110';

/**
 * Link hover - underline animation
 */
export const LINK_HOVER = 'relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-200 hover:after:w-full';

/**
 * Fade in from bottom - for page/section reveals
 */
export const FADE_IN_UP = 'animate-in fade-in-0 slide-in-from-bottom-4 duration-300';

/**
 * Fade in from top
 */
export const FADE_IN_DOWN = 'animate-in fade-in-0 slide-in-from-top-4 duration-300';

/**
 * Scale in - for modals, tooltips
 */
export const SCALE_IN = 'animate-in fade-in-0 zoom-in-95 duration-200';

/**
 * Scale out
 */
export const SCALE_OUT = 'animate-out fade-out-0 zoom-out-95 duration-150';

// =============================================================================
// STAGGERED ANIMATIONS
// =============================================================================

/**
 * Stagger delay classes for list item animations
 * Use with FADE_IN_UP for cascading reveal effects
 */
export const STAGGER_DELAY = {
  0: '',
  1: '[animation-delay:50ms]',
  2: '[animation-delay:100ms]',
  3: '[animation-delay:150ms]',
  4: '[animation-delay:200ms]',
  5: '[animation-delay:250ms]',
  6: '[animation-delay:300ms]',
  7: '[animation-delay:350ms]',
  8: '[animation-delay:400ms]',
  9: '[animation-delay:450ms]',
  10: '[animation-delay:500ms]',
} as const;

/**
 * Get stagger delay class for a given index
 */
export const getStaggerDelay = (index: number): string => {
  const maxIndex = Object.keys(STAGGER_DELAY).length - 1;
  const clampedIndex = Math.min(index, maxIndex) as keyof typeof STAGGER_DELAY;
  return STAGGER_DELAY[clampedIndex];
};

// =============================================================================
// INTERACTIVE STATES
// =============================================================================

/**
 * Interactive card - combines hover lift with focus ring
 */
export const INTERACTIVE_CARD = [
  'transition-all duration-200',
  'hover:-translate-y-0.5 hover:shadow-lg',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'cursor-pointer',
].join(' ');

/**
 * Subtle highlight on hover - for list items
 */
export const LIST_ITEM_HOVER = 'transition-colors duration-150 hover:bg-accent/50';

/**
 * Selected state - for navigation items
 */
export const SELECTED_STATE = 'bg-primary/10 text-primary';

/**
 * Disabled state overlay
 */
export const DISABLED_STATE = 'opacity-50 pointer-events-none cursor-not-allowed';

// =============================================================================
// LOADING STATES
// =============================================================================

/**
 * Spinning loader animation
 */
export const SPINNER = 'animate-spin';

/**
 * Pulsing dot animation for typing indicators
 */
export const PULSE_DOT = 'animate-pulse rounded-full bg-current';

/**
 * Bounce animation for attention
 */
export const BOUNCE = 'animate-bounce';

// =============================================================================
// ENTRANCE ANIMATIONS
// =============================================================================

/**
 * Page entrance animation
 */
export const PAGE_ENTER = 'animate-in fade-in-0 slide-in-from-bottom-2 duration-300 fill-mode-both';

/**
 * Modal entrance animation
 */
export const MODAL_ENTER = 'animate-in fade-in-0 zoom-in-95 duration-200';

/**
 * Toast entrance animation
 */
export const TOAST_ENTER = 'animate-in fade-in-0 slide-in-from-top-full duration-300';

/**
 * Drawer entrance animation
 */
export const DRAWER_ENTER = 'animate-in fade-in-0 slide-in-from-right duration-300';

// =============================================================================
// TAILWIND KEYFRAMES CONFIG
// Export for use in tailwind.config.js
// =============================================================================

/**
 * Custom keyframes to add to Tailwind config
 * These extend Tailwind's built-in animate-in/animate-out system
 */
export const customKeyframes = {
  'accordion-down': {
    from: { height: '0' },
    to: { height: 'var(--radix-accordion-content-height)' },
  },
  'accordion-up': {
    from: { height: 'var(--radix-accordion-content-height)' },
    to: { height: '0' },
  },
  // Collapsible expand/collapse (uses different CSS variable than accordion)
  'collapsible-down': {
    from: { height: '0' },
    to: { height: 'var(--radix-collapsible-content-height)' },
  },
  'collapsible-up': {
    from: { height: 'var(--radix-collapsible-content-height)' },
    to: { height: '0' },
  },
  // Skeleton shimmer for loading states
  shimmer: {
    '0%': { backgroundPosition: '-200% 0' },
    '100%': { backgroundPosition: '200% 0' },
  },
} as const;

/**
 * Custom animations to add to Tailwind config
 */
export const customAnimations = {
  'accordion-down': 'accordion-down 0.2s ease-out',
  'accordion-up': 'accordion-up 0.2s ease-out',
  'collapsible-down': 'collapsible-down 0.2s ease-out',
  'collapsible-up': 'collapsible-up 0.2s ease-out',
  shimmer: 'shimmer 2s linear infinite',
} as const;
